import { useQuery } from 'react-query';
import {
  getAnalyticsOverview,
  getMessageMetrics,
  getQueueStatus,
  getAnalyticsMessages,
  type AnalyticsChannel,
  type AnalyticsMessage,
  type AnalyticsRange,
} from '@/services/analyticsService';
import type { AnalyticsOverview, MessageMetrics, QueueStatus } from '@/types';

export function useAnalyticsOverview(params: { range: AnalyticsRange; channel: AnalyticsChannel }) {
  return useQuery<AnalyticsOverview>({
    queryKey: ['analytics-overview', params],
    queryFn: async () => {
      const res = await getAnalyticsOverview(params);
      // Backend data shape: { total_messages, failed_messages, total_contacts }
      const d = res.data as unknown as {
        total_messages: number;
        failed_messages: number;
        total_contacts: number;
      };
      const delivered = Math.max(0, (d.total_messages ?? 0) - (d.failed_messages ?? 0));
      const delivery_rate = d.total_messages ? Math.round((delivered / d.total_messages) * 100) : 0;
      const mapped: AnalyticsOverview = {
        period: params.range,
        messages: {
          sent: d.total_messages ?? 0,
          delivered,
          failed: d.failed_messages ?? 0,
          delivery_rate,
        },
        contacts: {
          total: d.total_contacts ?? 0,
          active: d.total_contacts ?? 0,
          opted_out: 0,
        },
        top_templates: [],
      };
      return mapped;
    },
    enabled: true,
  });
}

export function useMessageMetrics(params: { range: AnalyticsRange; channel: AnalyticsChannel }) {
  return useQuery<MessageMetrics>({
    queryKey: ['analytics-messages', params],
    queryFn: async () => {
      const res = await getMessageMetrics(params);
      // Backend data shape: Array<{ id, type, status, created_at }>
      const rows = (res.data as unknown as Array<{ created_at: string; status: string }> ) ?? [];

      const toDateKey = (iso: string) => new Date(iso).toISOString().slice(0, 10);
      const timelineMap: Record<string, { sent: number; delivered: number; failed: number; delivery_rate: number }> = {};

      let total_sent = 0;
      let total_delivered = 0;
      let total_failed = 0;

      for (const r of rows) {
        const key = r.created_at ? toDateKey(r.created_at) : new Date().toISOString().slice(0, 10);
        timelineMap[key] ||= { sent: 0, delivered: 0, failed: 0, delivery_rate: 0 };
        // Consideramos "sent" todo registro (envios efetuados), e entregas para status delivered/read
        timelineMap[key].sent += 1;
        total_sent += 1;
        if (r.status === 'delivered' || r.status === 'read' || r.status === 'sent') {
          // contamos "delivered" como entregues+lidos; se só houver "sent", conta como enviado (já incluído)
          if (r.status !== 'sent') {
            timelineMap[key].delivered += 1;
            total_delivered += 1;
          }
        }
        if (r.status === 'failed') {
          timelineMap[key].failed += 1;
          total_failed += 1;
        }
      }

      const timeline = Object.keys(timelineMap)
        .sort()
        .map((date) => {
          const t = timelineMap[date];
          const rateBase = t.sent || (t.delivered + t.failed);
          const delivery_rate = rateBase ? Math.round((t.delivered / rateBase) * 100) : 0;
          return { date, sent: t.sent, delivered: t.delivered, failed: t.failed, delivery_rate };
        });

      const average_delivery_rate = total_sent ? Math.round((total_delivered / total_sent) * 100) : 0;

      const mapped: MessageMetrics = {
        timeline,
        summary: {
          total_sent,
          total_delivered,
          total_failed,
          average_delivery_rate,
        },
      };
      return mapped;
    },
    enabled: true,
  });
}

export function useQueueStatus() {
  return useQuery<QueueStatus>({
    queryKey: ['queue-status'],
    queryFn: async () => {
      const res = await getQueueStatus();
      return res.data as QueueStatus;
    },
    refetchInterval: 5000, // Atualizar a cada 5 segundos
    enabled: true,
  });
}

export function useMessageList(params: { range: AnalyticsRange; channel: AnalyticsChannel }) {
  return useQuery<AnalyticsMessage[]>({
    queryKey: ['analytics-messages-list', params],
    queryFn: async () => {
      const res = await getAnalyticsMessages(params);
      return (res.data ?? []) as AnalyticsMessage[];
    },
    enabled: true,
  });
}
